import { Controller } from 'stimulus';

// The new Schedule's People and equipment strip (schedules/plan/_strip), the
// pane under the Job view's jobs: a row per person and piece of equipment on
// the Schedule with their days off and their load each day.
//
// - Pointing at a task row lights its person and equipment here, with the
//   task's days outlined; an Any of These task's other choices are half lit.
//   An open popover (schedule-plan) pins that until it closes.
// - Clicking a name spotlights their tasks above: a case under res:p:<id> or
//   res:m:<id> in #1712's spotlight, from the board's model.spots, so it
//   survives every refresh and Esc clears it with the chips.
// - The splitter above the strip resizes it (remembered per person; double-
//   click puts it back), its band folds it, and it scrolls sideways with the
//   jobs so the days line up.
// - Its chips (schedules/plan/_strip_bar, also atop People & equipment) pick
//   the strip: This job (in focus), Everyone, or a saved strip. Showing N of M
//   is a checklist whose ticks apply at once (the chip shows it changed); +
//   Save names the strip as it stands (ScheduleStripsController); a saved
//   chip's ⋯ saves changes, renames, deletes. The × on a row takes it out.
//   Choices are prefs (SchedulesController#prefs); the board re-renders.
//
// It sits beside #1712's schedule controller and schedule-plan on the same
// element. The strip is part of the board those swap in after every change,
// so it re-applies its state on schedule:spotlight, which #1712's controller
// sends after each new board and each spotlight change.
const MIN_H = 80;
const MAX_H = 900;
const DEFAULT_H = 250;
// The jobs above keep at least this much (their pane is 84vh less the strip,
// or in a page fitted to the window, the panes' height less the strip)
const JOBS_MIN = 220;
// The page fits the window at this size (_schedule_plan.scss, main.sch-window)
const FITS_WINDOW = '(min-width: 768px) and (min-height: 560px)';

export default class extends Controller {
  static targets = ['pop'];

  connect() {
    this.height = null; // once the person drags it
    this.hoverId = null;
    this.pinId = null;
    this.litId = null;
    this.stripTop = 0;
    this.resizing = null;
    this._sync = () => this.sync();
    this._over = (e) => this.onOver(e);
    this._scroll = (e) => this.onScroll(e);
    this._down = (e) => this.onDown(e);
    this._move = (e) => this.onMove(e);
    this._up = () => this.onUp();
    this._dbl = (e) => this.onDouble(e);
    this._click = (e) => this.onClick(e);
    this._change = (e) => this.onChange(e);
    this._input = (e) => this.onInput(e);
    this._submit = (e) => this.onSubmit(e);
    this._key = (e) => this.onKey(e);
    this._outside = (e) => this.onOutside(e);
    this.popMode = null;
    this.showQ = '';
    // strip choices go one at a time, in order; pendingSel is the ticks
    // sent but not yet drawn, which the next tick builds on
    this.queue = [];
    this.sending = false;
    this.pendingSel = null;
    this.element.addEventListener('schedule:spotlight', this._sync);
    this.element.addEventListener('mouseover', this._over);
    // (scroll doesn't bubble; its capture phase reaches this element)
    this.element.addEventListener('scroll', this._scroll, true);
    this.element.addEventListener('pointerdown', this._down);
    this.element.addEventListener('dblclick', this._dbl);
    this.element.addEventListener('click', this._click);
    this.element.addEventListener('change', this._change);
    this.element.addEventListener('input', this._input);
    this.element.addEventListener('submit', this._submit);
    document.addEventListener('keydown', this._key);
    // (a page fitted to the window: a resized window refits the strip)
    this._resize = () => { cancelAnimationFrame(this.resizeFrame); this.resizeFrame = requestAnimationFrame(() => this.refit()); };
    window.addEventListener('resize', this._resize);
    document.addEventListener('pointerdown', this._outside);
    document.addEventListener('pointermove', this._move);
    document.addEventListener('pointerup', this._up);
    document.addEventListener('pointercancel', this._up);
    this.sync();
  }

  disconnect() {
    window.removeEventListener('resize', this._resize);
    cancelAnimationFrame(this.resizeFrame);
    this.element.removeEventListener('schedule:spotlight', this._sync);
    this.element.removeEventListener('mouseover', this._over);
    this.element.removeEventListener('scroll', this._scroll, true);
    this.element.removeEventListener('pointerdown', this._down);
    this.element.removeEventListener('dblclick', this._dbl);
    this.element.removeEventListener('click', this._click);
    this.element.removeEventListener('change', this._change);
    this.element.removeEventListener('input', this._input);
    this.element.removeEventListener('submit', this._submit);
    document.removeEventListener('keydown', this._key);
    document.removeEventListener('pointerdown', this._outside);
    document.removeEventListener('pointermove', this._move);
    document.removeEventListener('pointerup', this._up);
    document.removeEventListener('pointercancel', this._up);
  }

  // #1712's controller on the same element: the model, the spotlight, prefs
  get board() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'schedule');
  }

  get plan() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'schedule-plan');
  }

  get panes() { return this.element.querySelector('.sch-panes.with-strip'); }

  get strip() { return this.element.querySelector('[data-strip]'); }

  get stripBox() { return this.element.querySelector('[data-strip-scroller]'); }

  get jobsBox() {
    const panes = this.panes;
    return panes ? panes.querySelector('.sch-scroller') : null;
  }

  // ----- after every new board, and every spotlight change -----

  sync() {
    // (an open Showing list follows the new board's ticks, in either
    // grouping, once every choice sent is drawn)
    if (!this.sending && !this.queue.length) {
      this.pendingSel = null;
      if (this.popMode === 'show' && this.popOpen) this.renderShowing(true);
    }
    const panes = this.panes;
    if (!panes) return;
    // (a height saved on a taller screen still leaves the jobs their room;
    // the saved height is kept for when the window grows again)
    if (!panes.dataset.fitted) {
      this.wanted = this.currentHeight();
      panes.dataset.fitted = '1';
    }
    panes.style.setProperty('--sch-strip-h', `${this.fit(this.height || this.wanted)}px`);
    const board = this.board;
    if (board) this.setFolded(board.collapsed.has('strip'));
    const box = this.stripBox;
    const jobs = this.jobsBox;
    if (box) {
      box.scrollTop = this.stripTop;
      if (jobs) box.scrollLeft = jobs.scrollLeft;
    }
    // (the rows may be new: light them again)
    this.litId = null;
    this.light(this.pinId || this.hoverId);
    this.markSpot();
  }

  // ----- folding -----

  toggle(e) {
    if (e) e.preventDefault();
    const board = this.board;
    if (!board) return;
    const folded = !board.collapsed.has('strip');
    if (folded) board.collapsed.add('strip'); else board.collapsed.delete('strip');
    this.setFolded(folded);
    board.savePrefs();
  }

  setFolded(folded) {
    const panes = this.panes;
    if (!panes) return;
    panes.classList.toggle('strip-folded', folded);
    const band = panes.querySelector('.sch-sbar');
    if (band) band.classList.toggle('open', !folded);
    const body = panes.querySelector('[data-strip-body]');
    if (body) body.hidden = folded;
    this.fitJobs();
  }

  // The jobs above give up only what the strip uses: a strip with a few rows
  // is shorter than its height
  fitJobs() {
    const panes = this.panes;
    const box = this.stripBox;
    if (panes && box) panes.style.setProperty('--sch-strip-used', `${box.offsetHeight}px`);
  }

  // ----- lighting a task's person and equipment -----

  // (while pinned it still follows the pointer, so letting go lights the row
  // under it)
  onOver(e) {
    if (this.resizing || (this.plan && this.plan.drag)) return;
    const row = e.target.closest('.sch-task[data-task-row]');
    if (row) this.hoverId = row.dataset.taskRow;
    // (kept while the pointer goes down to the strip to read it)
    else if (!e.target.closest('[data-strip-scroller], [data-strip-splitter], [data-target="schedule-plan.pop"]')) this.hoverId = null;
    if (!this.pinId) this.light(this.hoverId);
  }

  // An open popover or chip window pins its task (schedule-plan); null lets go
  pin(id) {
    this.pinId = id ? String(id) : null;
    this.light(this.pinId || this.hoverId);
    if (this.pinId) this.reveal();
  }

  light(id) {
    const strip = this.strip;
    if (!strip) return;
    const key = id ? String(id) : null;
    if (this.litId === key) return;
    this.litId = key;
    strip.querySelectorAll('.sch-strip-win').forEach((el) => el.remove());
    strip.querySelectorAll('.sch-srow.lit, .sch-srow.lit2').forEach((r) => r.classList.remove('lit', 'lit2'));
    strip.classList.remove('lit');
    const board = this.board;
    const t = key && board && board.tasksById ? board.tasksById[key] : null;
    if (!t) return;
    const on = t.users.map((uid) => `p:${uid}`).concat(t.equipment.map((mid) => `m:${mid}`));
    const alt = (t.pc || []).filter((uid) => t.users.indexOf(uid) < 0).map((uid) => `p:${uid}`)
      .concat((t.ec || []).filter((mid) => t.equipment.indexOf(mid) < 0).map((mid) => `m:${mid}`));
    const days = board.model.days;
    const dated = t.s !== null && t.s !== undefined && t.e !== null && t.e !== undefined;
    const span = dated && t.e >= 0 && t.s < days ? [Math.max(t.s, 0), Math.min(t.e, days - 1)] : null;
    let lit = 0;
    on.forEach((k) => {
      const row = strip.querySelector(`[data-strip-res="${k}"]`);
      if (!row) return;
      lit += 1;
      row.classList.add('lit');
      if (!span) return;
      const win = document.createElement('span');
      win.className = 'sch-strip-win';
      win.style.left = board.pct(span[0]);
      win.style.width = board.pctW(span[1] - span[0] + 1);
      row.querySelector('.sch-tl').appendChild(win);
    });
    alt.forEach((k) => {
      const row = strip.querySelector(`[data-strip-res="${k}"]`);
      if (row) { lit += 1; row.classList.add('lit2'); }
    });
    // (the others dim only when something here is lit)
    strip.classList.toggle('lit', lit > 0);
  }

  // Scrolls the strip, not the page, to the first lit row when it's out of view
  reveal() {
    const box = this.stripBox;
    const strip = this.strip;
    const body = strip && strip.querySelector('[data-strip-body]');
    if (!box || !body || body.hidden) return;
    const row = strip.querySelector('.sch-srow.lit, .sch-srow.lit2');
    if (!row) return;
    const band = strip.querySelector('.sch-sbar');
    const bandH = band ? band.offsetHeight : 0;
    if (row.offsetTop < box.scrollTop + bandH) box.scrollTop = row.offsetTop - bandH;
    else if (row.offsetTop + row.offsetHeight > box.scrollTop + box.clientHeight) {
      box.scrollTop = row.offsetTop + row.offsetHeight - box.clientHeight;
    }
  }

  // ----- clicks: a name, the chips, the popover -----

  onClick(e) {
    const t = e.target;
    let el = t.closest('[data-strip-chip]');
    if (el) { e.preventDefault(); this.pickChip(el.dataset.stripChip); return; }
    el = t.closest('[data-strip-menu]');
    if (el) { e.preventDefault(); this.openMenu(el); return; }
    el = t.closest('[data-strip-save]');
    if (el) { e.preventDefault(); this.openSave(); return; }
    el = t.closest('[data-strip-show]');
    if (el) { e.preventDefault(); this.openShowing(el); return; }
    el = t.closest('[data-strip-hide]');
    if (el) { e.preventDefault(); this.toggleItem(el.dataset.stripHide, false); return; }
    el = t.closest('[data-strip-close]');
    if (el) { e.preventDefault(); this.closeStripPop(); return; }
    el = t.closest('[data-strip-do]');
    if (el) { e.preventDefault(); this.doMenu(el.dataset.stripDo); return; }
    this.spotName(e);
  }

  // ----- spotlight by name -----

  spotName(e) {
    const btn = e.target.closest('[data-strip-spot]');
    if (!btn) return;
    e.preventDefault();
    const board = this.board;
    if (!board) return;
    const key = `res:${btn.dataset.stripSpot}`;
    const on = !board.spot.active[key];
    Object.keys(board.spot.active).forEach((k) => { if (k.indexOf('res:') === 0) delete board.spot.active[k]; });
    if (on) {
      board.spot.active[key] = true;
      board.spot.idx[key] = 0;
      board.spot.scroll = true;
    }
    board.renderChips();
    board.applySpotlight();
  }

  // The name spotlighted, marked in the strip, and the band says how to clear it
  markSpot() {
    const strip = this.strip;
    const board = this.board;
    if (!strip || !board) return;
    const keys = Object.keys(board.spot.active).filter((k) => k.indexOf('res:') === 0);
    strip.querySelectorAll('.sch-srow.sp').forEach((r) => r.classList.remove('sp'));
    const hint = strip.querySelector('[data-strip-hint]');
    if (!keys.length) {
      if (hint) hint.textContent = '';
      return;
    }
    const res = keys[0].slice(4);
    const row = strip.querySelector(`[data-strip-res="${res}"]`);
    if (row) row.classList.add('sp');
    if (!hint) return;
    const nameEl = row && row.querySelector('.sch-sn-name');
    const name = nameEl ? nameEl.textContent.trim() : '';
    const person = res.charAt(0) === 'p';
    const has = (board.allCases()[keys[0]] || []).length > 0;
    let text;
    if (has) text = person ? `Spotlight on ${name}'s tasks.` : `Spotlight on the tasks on ${name}.`;
    else text = `${name} has no open tasks on the jobs shown.`;
    hint.textContent = `${text} Click the name again or press Esc to clear.`;
  }

  // ----- the chips, Showing, + Save, a saved strip's menu -----

  get stripData() {
    const b = this.board;
    return b && b.model ? b.model.strip : null;
  }

  get popOpen() { return this.hasPopTarget && !this.popTarget.hidden; }

  get inFocus() { const d = this.stripData; return d && d.focus ? '1' : '0'; }

  pickChip(chip) {
    const d = this.stripData;
    if (d && d.chip === chip && !d.changed && !this.pendingSel) return;
    this.closeStripPop();
    this.pendingSel = null;
    this.sendPrefs({ strip: chip, in_focus: this.inFocus });
  }

  // Saves a strip choice, after any still on its way (ticks waiting to go
  // fold into the newest), then the board again once the last is in
  sendPrefs(body) {
    if (body.strip_custom) {
      const last = this.queue[this.queue.length - 1];
      if (last && last.strip_custom) this.queue.pop();
    }
    this.queue.push(body);
    if (!this.sending) this.nextPrefs();
  }

  nextPrefs() {
    const b = this.board;
    const body = this.queue.shift();
    if (!b) return;
    if (!body) { this.sending = false; b.refresh(); return; }
    this.sending = true;
    fetch(b.data.get('prefsUrl'), {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf() },
      body: JSON.stringify(body)
    }).then((r) => { if (!r.ok) throw new Error(r.status); })
      .catch(() => {
        this.queue = [];
        this.pendingSel = null;
        b.note('<span class="sch-conf">That strip could not be saved.</span>');
      })
      .then(() => this.nextPrefs());
  }

  // The selection as it stands, to change (a copy): the ticks still on
  // their way, or the board's
  currentSel() {
    const s = this.pendingSel || (this.stripData && this.stripData.sel) || {};
    return { people: (s.people || []).slice(), tags: (s.tags || []).slice(), machines: (s.machines || []).slice(), categories: (s.categories || []).slice() };
  }

  sendSel(sel) {
    this.pendingSel = sel;
    this.sendPrefs({ strip_custom: sel, in_focus: this.inFocus });
  }

  // Tick or untick one person or machine. Unticking one a group brought in
  // replaces the group with its other members.
  toggleItem(key, on) {
    const d = this.stripData;
    if (!d) return;
    const sel = this.currentSel();
    const id = Number(key.slice(2));
    if (key.charAt(0) === 'p') {
      if (on) { if (sel.people.indexOf(id) < 0) sel.people.push(id); } else {
        sel.people = sel.people.filter((x) => x !== id);
        const me = d.people.find((q) => q.id === id);
        (me ? me.tags : []).forEach((tag) => {
          if (sel.tags.indexOf(tag) < 0) return;
          sel.tags = sel.tags.filter((x) => x !== tag);
          d.people.forEach((q) => { if (q.id !== id && q.tags.indexOf(tag) >= 0 && sel.people.indexOf(q.id) < 0) sel.people.push(q.id); });
        });
      }
    } else if (on) { if (sel.machines.indexOf(id) < 0) sel.machines.push(id); } else {
      sel.machines = sel.machines.filter((x) => x !== id);
      const m = d.machines.find((x) => x.id === id);
      if (m && m.cat && sel.categories.indexOf(m.cat) >= 0) {
        sel.categories = sel.categories.filter((x) => x !== m.cat);
        d.machines.forEach((o) => { if (o.id !== id && o.cat === m.cat && sel.machines.indexOf(o.id) < 0) sel.machines.push(o.id); });
      }
    }
    this.sendSel(sel);
  }

  // A user tag (the team) or an equipment category (every Oven); unticking
  // takes its members out too. Ticked while every person (or every
  // machine) shows, it narrows that kind to the group.
  toggleGroup(kind, value, on) {
    const d = this.stripData;
    if (!d) return;
    const sel = this.currentSel();
    if (kind === 'tag') {
      if (on && !sel.tags.length && d.people.every((q) => sel.people.indexOf(q.id) >= 0)) sel.people = [];
      sel.tags = sel.tags.filter((x) => x !== value);
      if (on) sel.tags.push(value);
      else sel.people = sel.people.filter((id) => { const q = d.people.find((x) => x.id === id); return !q || q.tags.indexOf(value) < 0; });
    } else {
      const cat = Number(value);
      if (on && !sel.categories.length && d.machines.every((m) => sel.machines.indexOf(m.id) >= 0)) sel.machines = [];
      sel.categories = sel.categories.filter((x) => x !== cat);
      if (on) sel.categories.push(cat);
      else sel.machines = sel.machines.filter((id) => { const m = d.machines.find((x) => x.id === id); return !m || m.cat !== cat; });
    }
    this.sendSel(sel);
  }

  onChange(e) {
    const el = e.target;
    if (!this.hasPopTarget || !this.popTarget.contains(el)) return;
    if (el.dataset.stripItem) this.toggleItem(el.dataset.stripItem, el.checked);
    else if (el.dataset.stripGroup) this.toggleGroup(el.dataset.stripGroup, el.dataset.value, el.checked);
  }

  onInput(e) {
    if (!e.target.matches || !e.target.matches('[data-strip-search]')) return;
    this.showQ = e.target.value;
    this.renderShowing(true);
  }

  openShowing(anchor) {
    this.popMode = 'show';
    this.showQ = '';
    this.renderShowing(false);
    this.placeStripPop(anchor);
    const q = this.popTarget.querySelector('[data-strip-search]');
    if (q) q.focus();
  }

  renderShowing(listOnly) {
    const d = this.stripData;
    if (!d || !this.hasPopTarget) return;
    if (!listOnly) {
      this.popTarget.innerHTML = `
        <div class="spp-head"><div class="spp-title"><b>People and Equipment to Show</b><span>Ticks apply right away. Groups stay current: a new oven joins every Oven.</span></div>
          <button type="button" class="spp-x focus:outline-none" data-strip-close aria-label="Close">&times;</button></div>
        <input type="search" class="form-input sch-show-q" placeholder="Search name, tag or kind" autocomplete="off" aria-label="Search people and equipment" data-strip-search>
        <div class="sch-show-list" data-strip-list></div>
        <div class="spp-foot"><button type="button" class="spp-btn focus:outline-none" data-strip-close>Done</button>
          <button type="button" class="link focus:outline-none" data-strip-save>Save as a Strip…</button></div>`;
      this.popTarget.hidden = false;
    }
    const list = this.popTarget.querySelector('[data-strip-list]');
    if (list) list.innerHTML = this.showingHtml(d);
  }

  showingHtml(d) {
    const q = this.showQ.trim().toLowerCase();
    const hit = (text) => !q || text.toLowerCase().indexOf(q) >= 0;
    const shown = new Set(d.shown);
    const row = (attrs, checked, label, note) => `<label class="sch-show-row"><input type="checkbox" class="form-checkbox" ${attrs}${checked ? ' checked' : ''}>`
      + `<span class="sch-show-name">${this.esc(label)}</span><em>${this.esc(note || '')}</em></label>`;
    const named = (key) => (key.charAt(0) === 'p' ? d.people : d.machines).find((r) => r.id === Number(key.slice(2)));
    let h = '';
    if (d.focus) {
      const rows = d.suggest.map((sg) => { const r = named(sg.key); return r && hit(r.name) ? row(`data-strip-item="${sg.key}"`, false, r.name, sg.why) : ''; }).join('');
      if (rows) h += `<div class="sch-show-sec">Could Do These Tasks</div>${rows}`;
    }
    const groups = d.tags.filter((g) => hit(`${g.name} team`)).map((g) => row(`data-strip-group="tag" data-value="${this.esc(g.name)}"`, d.sel.tags.indexOf(g.name) >= 0, `${g.name} team`, `${g.n} ${g.n === 1 ? 'person' : 'people'}`))
      .concat(d.cats.filter((c) => hit(`every ${c.name}`)).map((c) => row(`data-strip-group="cat" data-value="${c.id}"`, d.sel.categories.indexOf(c.id) >= 0, `Every ${c.name}`, `${c.n} machines`)));
    if (groups.length) h += `<div class="sch-show-sec">Groups</div>${groups.join('')}`;
    const ppl = d.people.filter((p) => hit(`${p.name} ${p.tags.join(' ')}`));
    if (ppl.length) h += `<div class="sch-show-sec">People</div>${ppl.map((p) => row(`data-strip-item="p:${p.id}"`, shown.has(`p:${p.id}`), p.name, p.tags.join(', '))).join('')}`;
    const ms = d.machines.filter((m) => hit(`${m.name} ${m.cat_name || ''}`));
    if (ms.length) h += `<div class="sch-show-sec">Equipment</div>${ms.map((m) => row(`data-strip-item="m:${m.id}"`, shown.has(`m:${m.id}`), m.name, m.cat_name)).join('')}`;
    return h || `<div class="sch-show-empty">Nothing matches "${this.esc(this.showQ)}"</div>`;
  }

  // + Save (the strip as it stands), or Rename a saved one
  openSave(renameId, renameName) {
    const d = this.stripData;
    if (!d || !this.hasPopTarget) return;
    const anchor = this.element.querySelector('.sch-stripbar [data-strip-save]');
    this.popMode = 'save';
    this.renameId = renameId || null;
    const groups = d.sel.tags.length || d.sel.categories.length;
    const sub = renameId ? '' : `${d.shown.length} ${d.shown.length === 1 ? 'person or machine' : 'people and machines'}${groups ? '. Groups stay current.' : ''}`;
    this.popTarget.innerHTML = `<form data-strip-form>
      <div class="spp-head"><div class="spp-title"><b>${renameId ? `Rename ${this.esc(renameName)}` : 'Save This Strip'}</b><span>${this.esc(sub)}</span></div>
        <button type="button" class="spp-x focus:outline-none" data-strip-close aria-label="Close">&times;</button></div>
      <label class="sch-strip-label" for="sch-strip-name">Name</label>
      <input id="sch-strip-name" class="form-input sps-input" maxlength="60" autocomplete="off" required
             placeholder="e.g. Thermal lab, Mechanical, Dan's ovens" value="${renameId ? this.esc(renameName) : ''}">
      <p class="spp-err" data-strip-err hidden></p>
      <div class="spp-foot"><button type="submit" class="${this.data.get('buttonClass')}">Save</button>
        <button type="button" class="spp-btn focus:outline-none" data-strip-close>Cancel</button></div></form>`;
    this.popTarget.hidden = false;
    if (anchor) this.placeStripPop(anchor);
    this.popTarget.querySelector('#sch-strip-name').focus();
  }

  onSubmit(e) {
    if (!e.target.matches || !e.target.matches('[data-strip-form]')) return;
    e.preventDefault();
    const name = this.popTarget.querySelector('#sch-strip-name').value.trim();
    if (!name) { this.stripError('Give it a name.'); return; }
    if (this.renameId) this.sendStrip(this.data.get('stripUrl').replace('ID', this.renameId), 'PATCH', { name });
    else this.sendStrip(this.data.get('stripsUrl'), 'POST', { name, selection: this.currentSel(), in_focus: this.inFocus });
  }

  // A saved strip's ⋯: Save Changes (when its ticks have changed it), Rename, Delete
  openMenu(btn) {
    const d = this.stripData;
    if (!d || !this.hasPopTarget) return;
    const id = btn.dataset.stripMenu;
    this.popMode = 'menu';
    this.menuId = id;
    this.menuName = btn.dataset.stripName;
    const edited = d.chip === id && d.changed;
    this.popTarget.innerHTML = `<div class="spp-head"><div class="spp-title"><b>${this.esc(this.menuName)}</b></div>
        <button type="button" class="spp-x focus:outline-none" data-strip-close aria-label="Close">&times;</button></div>
      <div class="sch-strip-menu">
        ${edited ? `<button type="button" class="sch-strip-mi focus:outline-none" data-strip-do="update">Save Changes to ${this.esc(this.menuName)}</button>` : ''}
        <button type="button" class="sch-strip-mi focus:outline-none" data-strip-do="rename">Rename</button>
        <button type="button" class="sch-strip-mi red focus:outline-none" data-strip-do="delete">Delete</button>
      </div><p class="spp-err" data-strip-err hidden></p>`;
    this.popTarget.hidden = false;
    this.placeStripPop(btn);
  }

  doMenu(what) {
    const url = this.data.get('stripUrl').replace('ID', this.menuId);
    if (what === 'rename') this.openSave(this.menuId, this.menuName);
    else if (what === 'update') this.sendStrip(url, 'PATCH', { selection: this.currentSel() });
    else if (what === 'delete') this.sendStrip(url, 'DELETE', {});
  }

  sendStrip(url, method, body) {
    fetch(url, {
      method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': this.csrf() },
      body: JSON.stringify(body)
    }).then((r) => r.json().catch(() => ({ ok: false })))
      .then((json) => {
        if (!json.ok) { this.stripError(json.error || 'That could not be saved.'); return; }
        this.closeStripPop();
        const b = this.board;
        if (b) { b.note(this.esc((json.changes || []).join(' '))); b.refresh(); }
      })
      .catch(() => this.stripError('That could not be saved. Check your connection and try again.'));
  }

  stripError(text) {
    const el = this.hasPopTarget && this.popTarget.querySelector('[data-strip-err]');
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
  }

  placeStripPop(anchor) {
    const pop = this.popTarget;
    const r = anchor.getBoundingClientRect();
    const w = pop.offsetWidth;
    const h = pop.offsetHeight;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 16));
    let top = r.bottom + 6;
    if (top + h > window.innerHeight - 8) top = Math.max(72, r.top - h - 6);
    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }

  closeStripPop() {
    this.popMode = null;
    if (!this.hasPopTarget || this.popTarget.hidden) return;
    this.popTarget.hidden = true;
    this.popTarget.innerHTML = '';
  }

  onKey(e) {
    if (e.key === 'Escape' && this.popOpen) this.closeStripPop();
  }

  onOutside(e) {
    if (!this.popOpen || this.popTarget.contains(e.target)) return;
    if (e.target.closest('[data-strip-show], [data-strip-save], [data-strip-menu]')) return;
    this.closeStripPop();
  }

  csrf() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : '';
  }

  esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ----- resizing, and scrolling with the jobs -----

  onDown(e) {
    if (e.button !== 0) return;
    const bar = e.target.closest('[data-strip-splitter]');
    if (!bar || !this.panes) return;
    e.preventDefault();
    this.resizing = { y0: e.clientY, h0: this.currentHeight(), bar };
    bar.classList.add('on');
  }

  onMove(e) {
    if (!this.resizing) return;
    this.setHeight(this.resizing.h0 - (e.clientY - this.resizing.y0));
  }

  onUp() {
    if (!this.resizing) return;
    this.resizing.bar.classList.remove('on');
    this.resizing = null;
    this.save();
  }

  onDouble(e) {
    if (!e.target.closest('[data-strip-splitter]')) return;
    this.setHeight(DEFAULT_H);
    this.save();
  }

  currentHeight() {
    if (this.height) return this.height;
    const value = parseInt(getComputedStyle(this.panes).getPropertyValue('--sch-strip-h'), 10);
    return Number.isFinite(value) ? value : DEFAULT_H;
  }

  setHeight(h) {
    this.height = this.fit(h);
    const panes = this.panes;
    if (panes) panes.style.setProperty('--sch-strip-h', `${this.height}px`);
    this.fitJobs();
  }

  fit(h) {
    return Math.round(Math.max(MIN_H, Math.min(MAX_H, this.room() - JOBS_MIN, h)));
  }

  // The height the jobs and the strip share: the panes' own, where the page
  // fits the window; otherwise 84% of the window
  room() {
    const panes = this.panes;
    if (panes && this.element.classList.contains('sch-window-page') && window.matchMedia(FITS_WINDOW).matches) {
      const splitter = panes.querySelector('.sch-splitter');
      return panes.clientHeight - (splitter ? splitter.offsetHeight : 0);
    }
    return window.innerHeight * 0.84;
  }

  // The Key opened or closed: the strip refits, and the choice is saved
  // (not when the page itself opens it as the person left it)
  keyToggled(e) {
    this.refit();
    const key = e.currentTarget;
    if (String(key.open) === key.dataset.saved) return;
    key.dataset.saved = String(key.open);
    const board = this.board;
    if (board) board.savePrefs({ key_open: key.open });
  }

  refit() {
    const panes = this.panes;
    if (!panes) return;
    panes.style.setProperty('--sch-strip-h', `${this.fit(this.height || this.wanted || this.currentHeight())}px`);
    this.fitJobs();
  }

  save() {
    const board = this.board;
    if (board && this.height) board.savePrefs({ strip_height: this.height });
  }

  onScroll(e) {
    const el = e.target;
    if (!(el instanceof Element)) return;
    if (el.matches('[data-strip-scroller]')) {
      this.stripTop = el.scrollTop;
      const jobs = this.jobsBox;
      if (jobs && jobs.scrollLeft !== el.scrollLeft) jobs.scrollLeft = el.scrollLeft;
    } else if (el.matches('.sch-panes > .sch-scroller')) {
      const box = this.stripBox;
      if (box && box.scrollLeft !== el.scrollLeft) box.scrollLeft = el.scrollLeft;
    }
  }
}
